import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import OnboardingTable from 'features/Onboarding/components/OnboardingTable'
import IncompleteOnboardingBanner from 'features/Onboarding/components/IncompleteOnboardingBanner'
import styled from 'styled-components'
import WelcomeModal from 'features/Onboarding/components/WelcomeModal'
import AwaitingEmployerApprovalModal from 'features/Onboarding/components/AwaitingEmployerApprovalModal'
import { onboardingActions } from 'features/Onboarding/onboardingActions'
import { useDispatch } from 'react-redux'
import EmployeeDetailsDrawer from 'features/Onboarding/employee/EmployeeDetailsDrawer'
import ActionStatusModal from 'employee-platform/shared/components/ActionStatusModal'
import { Alert as AlertIcon } from 'core-system/Icons/Misc'
import employeePlatformService from 'redux/employeePlatform/employeePlatformService'
import palette from 'core-system/Themes/palette'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import { useGateValue } from '@statsig/react-bindings'
import {
  isBlankName,
  legalNameForProfileUpdate,
} from 'features/Onboarding/employee/EmployeeOnboardingUtils'

const Container = styled.div`
  padding: 2rem;
`

const EmployeeOnboardingView = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const shouldRedirectToMicromobilityAfterOnboarding = useGateValue(
    'redirect_to_micromobility_after_onboarding'
  )
  const onboarding = useSelector((state: AppState) => state.onboarding)

  const [updateUserInfoFailMsg, setUpdateUserInfoFailMsg] = useState<
    string | null
  >(null)
  const [acceptTermsFailMsg, setAcceptTermsFailMsg] = useState<string | null>(
    null
  )
  const { profileData, hasDOB } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const isAwaitingEmployerApproval = profileData?.approved === false
  const [additionalInformation, setAdditionalInformation] = useState<any>({
    stage: 'ADDITIONAL_INFORMATION',
    drawerOpen: false,
    status: 'PENDING',
    onActionButtonClick: () =>
      setAdditionalInformation({ ...additionalInformation, drawerOpen: true }),
    actionButtonDisabled: false,
    actionButtonLoading: false,
  })

  const [profileDataLocal, setProfileDataLocal] = useState<any>({
    homeAddress: profileData?.homeAddress || null,
    unitNumber: profileData?.unitNumber || null,
    employeeCorpId: profileData?.employeeCorpId || null,
    firstName: profileData?.firstName || '',
    lastName: profileData?.lastName || '',
    dob: {
      day: null,
      month: null,
      year: null,
    },
  })

  useEffect(() => {
    setProfileDataLocal({
      ...profileDataLocal,
      homeAddress: profileData?.homeAddress || null,
      unitNumber: profileData?.unitNumber || null,
      employeeCorpId: profileData?.employeeCorpId || null,
      firstName: profileDataLocal.firstName || profileData?.firstName || '',
      lastName: profileDataLocal.lastName || profileData?.lastName || '',
    })
  }, [profileData])

  const invalidDOB = useMemo(() => {
    return (
      !profileDataLocal.dob?.day ||
      !profileDataLocal.dob?.month ||
      !profileDataLocal.dob?.year
    )
  }, [profileDataLocal.dob])

  const getProfileKeyValue = (key: string) => {
    return profileDataLocal[key] && profileDataLocal[key] !== ''
      ? profileDataLocal[key]
      : profileData?.[key]
  }

  // 7 days after the employee joined
  const dueDate = profileData?.dateJoined
    ? moment(profileData.dateJoined).add(7, 'days').format('MM/DD/YYYY')
    : 'N/A'

  const buildProfileUpdatePayload = (onboardingComplete: boolean) => {
    const legalName = legalNameForProfileUpdate(
      profileData?.firstName,
      profileData?.lastName,
      profileDataLocal.firstName,
      profileDataLocal.lastName
    )
    const updateProfileReqData = {
      ...profileData,
      ...legalName,
      homeAddress: getProfileKeyValue('homeAddress'),
      unitNumber: getProfileKeyValue('unitNumber'),
      employeeCorpId: getProfileKeyValue('employeeCorpId'),
      onboardingComplete,
    }
    // Remove avatar from the request data b/c it can cause a crash when onboarding after social login
    delete updateProfileReqData.avatar
    return updateProfileReqData
  }

  const updateEmployeeProfile = async () => {
    try {
      setAdditionalInformation({
        ...additionalInformation,
        actionButtonLoading: true,
      })
      // Do not set onboardingComplete yet — match mobile (FLE-851): cardholder/card
      // must succeed before flipping onboarding_complete (which triggers issuance signal).
      const updateProfileDataResponse =
        await employeePlatformService.updateProfileData(
          buildProfileUpdatePayload(false)
        )

      setAdditionalInformation({
        ...additionalInformation,
        actionButtonLoading: false,
      })
      if (updateProfileDataResponse.status === 200) {
        setAdditionalInformation({
          ...additionalInformation,
          actionButtonDisabled: true,
          stage: 'TERMS_AND_CONDITIONS',
        })
      } else {
        setUpdateUserInfoFailMsg(
          'Failed to update your profile information. Please try again or contact support@movewithfleet.com'
        )
      }
    } catch (e: any) {
      setAdditionalInformation({
        ...additionalInformation,
        actionButtonLoading: false,
      })
      setUpdateUserInfoFailMsg(
        'Failed to update your profile information. Please try again or contact support@movewithfleet.com'
      )
    }
  }

  const disableSubmitButton =
    !profileDataLocal.homeAddress ||
    (!profileData?.cardholderVerified && !hasDOB && invalidDOB) ||
    (!hasDOB &&
      !invalidDOB &&
      moment().diff(
        moment(
          `${profileDataLocal.dob.day}/${profileDataLocal.dob.month}/${profileDataLocal.dob.year}`,
          'DD/MM/YYYY'
        ),
        'years'
      ) < 18) ||
    !profileDataLocal.employeeCorpId ||
    isBlankName(profileDataLocal.firstName) ||
    isBlankName(profileDataLocal.lastName)

  const acceptTerms = async (termsAcceptedDate: number) => {
    try {
      const cardholderDetailsRequestData = {
        dob: moment(
          `${profileDataLocal.dob.day}/${profileDataLocal.dob.month}/${profileDataLocal.dob.year}`,
          'DD/MM/YYYY'
        ).format('YYYY-MM-DD'),
        user_terms_acceptance: termsAcceptedDate,
        billing_address:
          profileDataLocal.homeAddress && profileDataLocal.homeAddress !== ''
            ? profileDataLocal.homeAddress
            : profileData?.homeAddress,
      }
      const cardholderDetailsRequestResponse =
        await employeePlatformService.updateCardholderDetails(
          cardholderDetailsRequestData
        )
      if (cardholderDetailsRequestResponse.status === 200) {
        return 'Success'
      } else {
        return 'Failed'
      }
    } catch (_e: any) {
      setAcceptTermsFailMsg(
        'Something went wrong when trying to verify your details. Please try again or contact support@movewithfleet.com'
      )
      return 'Failed'
    }
  }

  const completeOnboardingSuccess = () => {
    dispatch(onboardingActions.updateIsEmployeeOnboarding(false))
    dispatch(onboardingActions.updateJustCompletedOnboarding(true))
    setAdditionalInformation({
      ...additionalInformation,
      actionButtonDisabled: true,
      actionButtonLoading: false,
      stage: 'SUCCESS',
      drawerOpen: false,
    })
    if (shouldRedirectToMicromobilityAfterOnboarding) {
      navigate(Locations.EmployeeMicromobility.Overview, {
        replace: true,
        state: { firstTime: true },
      })
    }
  }

  const finishOnboarding = async () => {
    setAdditionalInformation({
      ...additionalInformation,
      actionButtonLoading: true,
    })
    try {
      // Submit already saved the profile, including the legal name, before this
      // agreement. FLE-851: create the card first, then set onboardingComplete.
      if (profileData?.approved && !profileData?.cardholderVerified) {
        const legalName = legalNameForProfileUpdate(
          profileData?.firstName,
          profileData?.lastName,
          profileDataLocal.firstName,
          profileDataLocal.lastName
        )
        if (!legalName.lastName) {
          setAdditionalInformation({
            ...additionalInformation,
            actionButtonLoading: false,
          })
          setAcceptTermsFailMsg(
            'A last name is required before your card can be created. Please go back and add it.'
          )
          return
        }
        const acceptTermsResult = await acceptTerms(moment().unix())
        if (acceptTermsResult === 'Failed') {
          setAdditionalInformation({
            ...additionalInformation,
            actionButtonLoading: false,
          })
          return
        }
      }

      const updateProfileDataResponse =
        await employeePlatformService.updateProfileData(
          buildProfileUpdatePayload(true)
        )
      if (updateProfileDataResponse.status !== 200) {
        setAdditionalInformation({
          ...additionalInformation,
          actionButtonLoading: false,
        })
        setUpdateUserInfoFailMsg(
          'Failed to update your profile information. Please try again or contact support@movewithfleet.com'
        )
        return
      }
      completeOnboardingSuccess()
    } catch (_e: any) {
      setAdditionalInformation({
        ...additionalInformation,
        actionButtonLoading: false,
      })
      setUpdateUserInfoFailMsg(
        'Failed to update your profile information. Please try again or contact support@movewithfleet.com'
      )
    }
  }

  return (
    <>
      <IncompleteOnboardingBanner />
      <Container>
        <OnboardingTable
          rows={[
            {
              owner: 'You',
              title: 'Additional Information',
              description: 'Add additional information to your profile',
              status: additionalInformation.status,
              dueDate: dueDate,
              actionButtonText: 'Complete',
              onActionButtonClick: additionalInformation.onActionButtonClick,
              actionButtonDisabled:
                isAwaitingEmployerApproval ||
                additionalInformation.actionButtonDisabled,
            },
          ]}
        />
      </Container>
      <WelcomeModal
        open={!isAwaitingEmployerApproval && !onboarding.welcomeModalShown}
        onClose={() =>
          dispatch(onboardingActions.updateWelcomeModalShown(true))
        }
        type='employee'
      />
      <AwaitingEmployerApprovalModal open={isAwaitingEmployerApproval} />
      <EmployeeDetailsDrawer
        stage={additionalInformation.stage}
        isOpen={
          !isAwaitingEmployerApproval && additionalInformation.drawerOpen
        }
        onClose={() =>
          setAdditionalInformation({
            ...additionalInformation,
            drawerOpen: false,
          })
        }
        finishOnboarding={finishOnboarding}
        loading={additionalInformation.actionButtonLoading}
        updateEmployeeProfile={updateEmployeeProfile}
        profileDataLocal={profileDataLocal}
        setProfileDataLocal={setProfileDataLocal}
        disableSubmitButton={disableSubmitButton}
      />
      {/* MODAL EXPLAINING WHICH STEP WENT WRONG IF FAILURE */}
      <ActionStatusModal
        open={updateUserInfoFailMsg || acceptTermsFailMsg ? true : false}
        closeModal={() => {
          if (updateUserInfoFailMsg) {
            setUpdateUserInfoFailMsg(null)
          }
          if (acceptTermsFailMsg) {
            setAcceptTermsFailMsg(null)
          }
        }}
        text={updateUserInfoFailMsg || acceptTermsFailMsg}
        icon={
          <AlertIcon
            color={palette.error.red500}
            width={75}
            height={75}
            style={{ marginBottom: '1rem' }}
          />
        }
        buttonText='Try Again'
      />
    </>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  EmployeeOnboardingView.displayName = 'EmployeeOnboardingView'
}

export default EmployeeOnboardingView
